import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Plus, Loader2, Lock, Check, X } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { needsUpgrade, openUpgrade } from "@/lib/planFeatures";
import { formatCurrency } from "@/lib/format";
import api from "@/lib/api";

const EMPTY = { name: "", type: "fund", target_size: "", vintage_year: new Date().getFullYear(), mgmt_fee_pct: 2, carry_pct: 20, hurdle_pct: 8, deal_name: "", description: "" };

function GpTour({ steps, onStart, onOpenFund, onDismiss }) {
  const current = steps.findIndex((s) => !s.done);
  return (
    <Card className="mb-6 rounded-xl border border-brand/30 bg-brand/[0.05] p-5" data-testid="gp-tour-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-foreground">Get your first vehicle running</div>
          <p className="text-sm text-muted-foreground">Three steps from zero to your first capital call.</p>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={onDismiss} data-testid="gp-tour-dismiss" aria-label="Dismiss tour"><X className="h-4 w-4" /></Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {steps.map((s, i) => (
            <div key={s.title} className={`rounded-lg border bg-white p-3.5 ${i === current ? "border-brand/40 shadow-sm" : "border-slate-200"}`} data-testid={`gp-tour-step-${i + 1}`}>
            <div className="flex items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${s.done ? "bg-emerald-100 text-emerald-700" : i === current ? "bg-brand text-brand-foreground" : "bg-slate-100 text-slate-500"}`}>
                {s.done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={`text-sm font-semibold ${s.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{s.title}</span>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">{s.desc}</p>
            {i === current && (
              <Button size="sm" className="mt-2.5 h-7 text-xs" onClick={i === 0 ? onStart : onOpenFund} data-testid={`gp-tour-cta-${i + 1}`}>{s.cta}</Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Funds() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [funds, setFunds] = useState(null);
  const [gated, setGated] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tourDismissed, setTourDismissed] = useState(true);

  useEffect(() => { if (company) setTourDismissed(!!localStorage.getItem(`tenx_gp_tour_${company.id}`)); }, [company]);

  const load = async () => {
    if (!company) return;
    try {
      const { data } = await api.get(`/companies/${company.id}/funds`, { quiet402: true });
      setFunds(data); setGated(false);
    } catch (e) {
      if (e?.response?.status === 402) { setGated(true); setFunds([]); }
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.name.trim()) { toast.error("Give your vehicle a name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/funds`, {
        ...form, target_size: Number(form.target_size) || 0, vintage_year: Number(form.vintage_year) || null,
        mgmt_fee_pct: Number(form.mgmt_fee_pct) || 0, carry_pct: Number(form.carry_pct) || 0, hurdle_pct: Number(form.hurdle_pct) || 0,
      });
      toast.success(`${data.name} created`);
      setOpen(false); setForm(EMPTY);
      navigate(`/app/funds/${data.id}`);
    } catch (e) { if (e?.response?.status !== 402) toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  return (
    <AppShell title="Funds & SPVs" subtitle="Run your funds and sidecar vehicles - LPs, capital calls, distributions and performance."
      actions={<Button className="gap-1.5" onClick={() => (needsUpgrade(company?.plan, "scale") ? openUpgrade({ plan: "scale" }) : setOpen(true))} data-testid="new-fund-btn"><Plus className="h-4 w-4" /> New fund / SPV</Button>}>
      {!gated && funds && !tourDismissed && !(funds.length > 0 && funds.some((f) => f.metrics.lp_count > 0) && funds.some((f) => f.metrics.called > 0)) && (
        <GpTour
          steps={[
            { title: "Create a vehicle", desc: "A blind-pool fund or a single-deal SPV with your fee and carry terms.", cta: "Create vehicle", done: funds.length > 0 },
            { title: "Add an LP commitment", desc: "Record who's in and for how much - invite them to their LP portal later.", cta: "Add an LP", done: funds.some((f) => f.metrics.lp_count > 0) },
            { title: "Issue a capital call", desc: "Call a % of commitments pro-rata; every LP gets a notice with wire details.", cta: "Issue a call", done: funds.some((f) => f.metrics.called > 0) },
          ]}
          onStart={() => setOpen(true)}
          onOpenFund={() => funds[0] && navigate(`/app/funds/${funds[0].id}`)}
          onDismiss={() => { localStorage.setItem(`tenx_gp_tour_${company.id}`, "1"); setTourDismissed(true); }}
        />
      )}
      {gated && (
        <Card className="flex flex-col items-center gap-3 rounded-xl border-dashed p-10 text-center" data-testid="funds-gate">
          <Lock className="h-8 w-8 text-muted-foreground" />
          <p className="max-w-md text-sm text-muted-foreground">Funds & SPVs - Carta-style fund administration - is a Scale plan feature. Manage LP commitments, capital calls, waterfalls and IRR in one place.</p>
          <Button onClick={() => openUpgrade({ plan: "scale" })} data-testid="funds-upgrade-btn">Upgrade to Scale</Button>
        </Card>
      )}
      {!gated && funds && funds.length === 0 && tourDismissed && (
        <Card className="flex flex-col items-center gap-3 rounded-xl border-dashed p-10 text-center" data-testid="funds-empty">
          <Landmark className="h-8 w-8 text-muted-foreground" />
          <p className="max-w-md text-sm text-muted-foreground">No vehicles yet. Create your first fund or single-deal SPV - add LPs, call capital, track investments and distribute with a proper waterfall.</p>
          <Button className="gap-1.5" onClick={() => setOpen(true)} data-testid="funds-empty-create"><Plus className="h-4 w-4" /> Create your first vehicle</Button>
        </Card>
      )}
      {!gated && funds && funds.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {funds.map((f) => (
            <Card key={f.id} className="cursor-pointer rounded-xl border p-5 transition-shadow hover:shadow-md" onClick={() => navigate(`/app/funds/${f.id}`)} data-testid={`fund-card-${f.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">{f.name}</span>
                    <span data-testid={`fund-type-${f.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${f.type === "spv" ? "bg-brand/10 text-brand-ink" : "bg-secondary text-foreground"}`}>{f.type}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{f.vintage_year ? `Vintage ${f.vintage_year} · ` : ""}{f.metrics.lp_count} LP{f.metrics.lp_count === 1 ? "" : "s"} · {f.metrics.investment_count} holding{f.metrics.investment_count === 1 ? "" : "s"}</div>
                </div>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">{f.status}</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[["Committed", formatCurrency(f.metrics.committed)], ["NAV", formatCurrency(f.metrics.nav)], ["TVPI", f.metrics.tvpi != null ? `${f.metrics.tvpi}x` : "-"]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 p-2">
                    <div className="text-sm font-bold text-foreground">{v}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-fund-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-primary" /> New fund or SPV</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Acme Ventures Fund I / Acme–DealCo SPV" className="mt-1" data-testid="fund-form-name" /></div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1" data-testid="fund-form-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fund">Fund (blind pool)</SelectItem><SelectItem value="spv">SPV / Sidecar (single deal)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Vintage year</Label><Input type="number" value={form.vintage_year} onChange={(e) => set("vintage_year", e.target.value)} className="mt-1" data-testid="fund-form-vintage" /></div>
            {form.type === "spv" && (
              <div className="col-span-2"><Label>Target deal / company</Label><Input value={form.deal_name} onChange={(e) => set("deal_name", e.target.value)} placeholder="DealCo Inc. Series A" className="mt-1" data-testid="fund-form-deal" /></div>
            )}
            <div><Label>Target size ($)</Label><Input type="number" value={form.target_size} onChange={(e) => set("target_size", e.target.value)} placeholder="10000000" className="mt-1" data-testid="fund-form-target" /></div>
            <div><Label>Mgmt fee % / yr</Label><Input type="number" step="0.1" value={form.mgmt_fee_pct} onChange={(e) => set("mgmt_fee_pct", e.target.value)} className="mt-1" data-testid="fund-form-fee" /></div>
            <div><Label>Carry %</Label><Input type="number" step="0.5" value={form.carry_pct} onChange={(e) => set("carry_pct", e.target.value)} className="mt-1" data-testid="fund-form-carry" /></div>
            <div><Label>Hurdle (pref) %</Label><Input type="number" step="0.5" value={form.hurdle_pct} onChange={(e) => set("hurdle_pct", e.target.value)} className="mt-1" data-testid="fund-form-hurdle" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="fund-form-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="gap-2" data-testid="fund-form-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
